import React, {useState, useEffect} from "react";
import PropTypes from "prop-types";
import BookingService from "../services/BookingService";
import {withNamespaces} from "react-i18next";
import MoneyHelper from "../../../services/MoneyHelper";
import LoadingComponent from "../../forms/components/LoadingComponent/LoadingComponent";
import PaymentFormComponent from "../../payments/components/PaymentForm/PaymentForm.component";
import PaymentMethod from "../../payments/models/PaymentMethod";
import FocusAreaStep from "./FocusAreaStep.component";

const PaymentStep = withNamespaces()((props) => {
  return (
    <div className="payment-step">
      <h3>
        { props.event && (
          <span className='red-text'>{props.t('booking_step_3')}</span>
        )}
        {props.t('booking_payment_options')}
      </h3>
      <p className="bold">{props.t('booking_price', {price: MoneyHelper.toPrice(props.addon.price)})}</p>
      <p>{props.t('booking_payment_select_method')}</p>

      <PaymentFormComponent allowChangeCard={props.allowChangeCard} onChange={props.onChange} default_card={props.default_card}
                            paymentForm={props.payment_method}/>
    </div>
  );
});

PaymentStep.propTypes = {
  onChange: PropTypes.func.isRequired,
  payment_method: PropTypes.object,
  default_card: PropTypes.object,
  addon: PropTypes.object,
  allowChangeCard: PropTypes.bool,
};

const SelectTimeStep = withNamespaces()((props) => {
  const passed = {};
  const uniqueSlots = props.slots
    .sort((a, b) => a.start_date_time - b.start_date_time)
    .map(slot => {
      if (passed.hasOwnProperty(slot.getStartTime())) {
        return null;
      }
      passed[slot.getStartTime()] = true;
      return slot;
    })
    .filter(slot => slot !== null);
  return (
    <div className="select-time-step">
      <h3><span className='red-text'>{props.t(props.stepLabel || 'booking_step_2')}</span>{props.t('booking_select_time')}</h3>

      <div className='day-selection'>
        {uniqueSlots.map((slot, index) => (
          <a className={`day-item short selectable-item${slot === props.selectedSlot ? ' selected' : ''}`}
             key={index}
             onClick={() => props.onChange(slot)}>
            {slot.getStartTime().replace(" ", "")}
          </a>
        ))}
      </div>
      {props.slots.length === 0 && props.selectedDate && (<p>{props.t('booking_session_limit')}</p>)}
    </div>
  );
});

SelectTimeStep.propTypes = {
  onChange: PropTypes.func.isRequired,
  selectedSlot: PropTypes.object,
  slots: PropTypes.array,
  selectedDate: PropTypes.object,
};

const SelectDateStep = withNamespaces()((props) => {
  const days = Array.from({length: 5}, (_, index) => {
    const day = new Date(props.startingDate);
    day.setDate(day.getDate() + index);
    return day;
  });
  return (
    <div className="select-date-step step">
      <h3><span className='red-text'>{props.t('booking_step_1')}</span>{props.t('booking_select_date')}</h3>
      <p>{props.t(`booking_session_limits`)}</p>

      <div className='day-selection'>
        {days.map((date, index) => (
          <a
            className={`day-item selectable-item${props.selectedDate && date.getDate() === props.selectedDate.getDate() ? ' selected' : ''}`}
            key={index}
            onClick={() => props.onChange(date)}>

            {date.toLocaleDateString("en-us", {weekday: 'long', month: 'long', day: 'numeric'})}
          </a>
        ))}
      </div>
    </div>
  );
});

SelectDateStep.propTypes = {
  startingDate: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  selectedDate: PropTypes.object,
};

const TrainerDayDateStep = withNamespaces()((props) => {
  return (
    <div className="select-date-step step">
      <h3>{props.t('trainer_day_date_text', {date: props.trainerDayDate})}</h3>
    </div>
  );
});

const BookingSchedulerComponent = (props) => {
  const [selectedSlot, setSelectedSlot] = useState();
  const {event, addon, availability, card, allowPreSale} = props;
  const [result, setResult] = useState(null);
  const isTrainerDay = props.eventType === 'training-day';
  const focusEnabled = isTrainerDay && props.focusEnabled === true;
  const basePath = props.basePath || '/booking/crunchone';

  const minAvailabilityStart = availability.length > 0 ? Math.min(...availability.map(a => a.start_date_time)) : null;
  const minDate = minAvailabilityStart ? new Date(minAvailabilityStart) : new Date();
  const [selectedDate, setSelectedDate] = useState(minDate);
  const [terms, setTerms] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(new PaymentMethod());
  const [loading, setLoading] = useState(false);
  const [selectedFocusArea, setSelectedFocusArea] = useState(null);

  useEffect(() => {
    if (isTrainerDay && availability.length > 0 && !selectedDate) {
      setSelectedDate(minDate);
    }
  }, [availability]);

  const changeDate = (date) => {
    setSelectedDate(date);
    setSelectedSlot(undefined);
  }

  const book = () => {
    if (!selectedSlot && !allowPreSale) {
      console.error('Please select a Slot');
      return;
    }
    setLoading(true);
    BookingService.bookEvent(
      event.event_type_id, selectedSlot, addon,
      {use_default: selectedPayment.use_default, ...selectedPayment.credit_card},
      focusEnabled ? selectedFocusArea : null,
      basePath
    ).then(response => {
        setLoading(false);
        if (response.result.status === false) {
          setResult({success: false, error: response.result.message, bought: response.result.bought});
        } else {
          window.scrollTo(0, 0);
          props.onSuccess({success: true, event: event, slot: selectedSlot, focusArea: focusEnabled ? selectedFocusArea : null});
        }
      }).catch(() => setLoading(false));
  };

  const isValid = () => {
    if (isTrainerDay) {
      const baseValid = (!event.shouldBuy() || !addon || terms) && selectedSlot;
      return focusEnabled ? (baseValid && selectedFocusArea) : baseValid;
    }
    return (selectedSlot || allowPreSale) && (!hasPurchase || terms);
  };

  const slots = availability.filter(a => selectedDate && a.start_date_time.getDate() === selectedDate.getDate());
  const hasPurchase = (event && event.shouldBuy() && addon) || (addon && allowPreSale);

  return (event || addon) ? (
    <div className='booking-scheduler-component'>
      {loading && <LoadingComponent isVisible={loading}/>}
      <div className='booking-scheduler-container'>
        {isTrainerDay ? (
          <React.Fragment>
            <div className='schedule-step'>
              <TrainerDayDateStep trainerDayDate={props.trainerDayDate} />
            </div>

            {focusEnabled && (
              <div className='schedule-step'>
                <FocusAreaStep selectedFocusArea={selectedFocusArea} onChange={(area) => setSelectedFocusArea(area)} />
              </div>
            )}

            <div className='schedule-step'>
              <SelectTimeStep
                selectedDate={selectedDate}
                slots={slots}
                selectedSlot={selectedSlot}
                stepLabel={focusEnabled ? 'trainer_day_step_3' : 'trainer_day_step_2'}
                onChange={(slot) => setSelectedSlot(slot)}/>
            </div>
          </React.Fragment>
        ) : !allowPreSale && event && (
          <React.Fragment>
            <div className='schedule-step'>
              <SelectDateStep startingDate={minDate} selectedDate={selectedDate} onChange={(date) => changeDate(date)}/>
            </div>

            <div className='schedule-step'>
              <SelectTimeStep
                selectedDate={selectedDate}
                slots={slots}
                selectedSlot={selectedSlot}
                stepLabel={'booking_step_2'}
                onChange={(slot) => setSelectedSlot(slot)}/>
            </div>
          </React.Fragment>
        )}

        {hasPurchase && (
          <div className='schedule-step'>
            <PaymentStep default_card={card}
                         addon={addon}
                         payment_method={selectedPayment}
                         allowChangeCard={props.allowChangeCard}
                         onChange={(payment) => setSelectedPayment(payment)}/>
          </div>
        )}

        {result && result.error && <p className='red-text'>{result.error}</p>}
        {result && result.success && <p>Success</p>}

        <div className='schedule-terms'>
          {hasPurchase && (
            <div className="checkbox">
              <input
                id={`accept_terms`}
                name={`accept_terms`}
                type="checkbox"
                checked={terms}
                onChange={() => {
                  setTerms(!terms)
                }}/>
              <label htmlFor={`accept_terms`}>
                <div dangerouslySetInnerHTML={{
                  __html: props.t(`booking_terms_conditions`, {
                    total: addon ? MoneyHelper.toPrice(addon.getTotal()) : 0
                  })
                }}/>
              </label>
            </div>
          )}
          <button className='form-main-button'
                  disabled={!isValid()}
                  onClick={(e) => book()}>
            {props.t(hasPurchase ? (allowPreSale ? 'pay_now' : 'book_now_and_pay') : 'book_now')}
          </button>
        </div>

        {isTrainerDay && props.ptPromoEnabled && (
          <div className='c1ko-promo'>
            <p className="c1ko-heading">{props.t('trainer_day_c1ko_heading_1')}</p>
            <p className="c1ko-heading">{props.t('trainer_day_c1ko_heading_2')}</p>
            <a href='/booking/crunchone' className='book-now'>
              {props.t('trainer_day_c1ko_cta')}
            </a>
          </div>
        )}
      </div>
    </div>
  ) : (<div>{props.t('booking_no_event_available')}</div>);
};

BookingSchedulerComponent.propTypes = {
  onSuccess: PropTypes.func,
  allowChangeCard: PropTypes.bool,
  event: PropTypes.object,
  availability: PropTypes.array,
  addon: PropTypes.object,
  card: PropTypes.object,
  allowPreSale: PropTypes.bool
};

export default withNamespaces()(BookingSchedulerComponent);